import { useCallback } from 'react';
import { useSpring, config } from '@react-spring/web';
import { supportsPassiveEvents } from '.';

const eventOptions = supportsPassiveEvents ? { passive: true } : false;

/**
 * ! important: will be aborted if component is rerendered while scrolling or on user interacts with page
 * use like
 * ```javascript
 * let { scrollTo } = useScrollToElement({yOffset: 100}) // predefined to scroll to 100 pixels below header
 * let elemRef = useRef()
 * scrollTo(elemRef.current)
 * ```
 */
export function useScrollToElement({ xOffset = 0, yOffset = 0 } = {}, springConfig = config.slow) {
  let [, setScrollPos] = useSpring(() => ({ from: { x: 0, y: 0 }, config: springConfig }), []);
  let immediateStopScroll = useCallback(() => setScrollPos.stop(), [setScrollPos]);

  let scrollTo = useCallback(
    /** @param {React.MutableRefObject<HTMLElement>|HTMLElement} node */
    node => {
      /** @type HTMLElement */
      let currentNode = 'current' in node ? node.current : node;
      if (!currentNode) return;

      window.addEventListener('wheel', immediateStopScroll, eventOptions);
      window.addEventListener('touchmove', immediateStopScroll, eventOptions);
      window.addEventListener('keydown', immediateStopScroll, eventOptions);

      let boundingRect = currentNode.getBoundingClientRect();
      setScrollPos.start({ reset: true, from: { x: window.pageXOffset, y: window.pageYOffset } });
      setScrollPos.start({
        y: boundingRect.top + window.pageYOffset - yOffset,
        x: boundingRect.left + window.pageXOffset - xOffset,
        onChange: ({ value: { x, y } }) => window.scrollTo(x, y),
        onRest: () => {
          window.removeEventListener('wheel', immediateStopScroll);
          window.removeEventListener('touchmove', immediateStopScroll);
          window.removeEventListener('keydown', immediateStopScroll);
        },
      });
    },
    [immediateStopScroll, setScrollPos, yOffset, xOffset]
  );

  return { scrollTo };
}
